/**
 * Concrete dep-injection wiring for the entity-layer widget hooks.
 *
 * Lives in the APP layer (`app/store/widget/`) because it value-imports from
 * `@/features/artifacts/api` (cross-feature OUT) and `@/app/store/widget` —
 * sources the entity-layer version hooks may not reach without inverting the
 * FSD layer order.
 *
 * Consumed by widget feature slices that previously imported from
 * `@/features/widgets/hooks/...`. Migrated to a single barrel here
 * (umbrella cleanup).
 *
 * Re-exposes the bound hooks under their legacy names (`useWidgetVersions`,
 * `useCreateArtifactVersion`) so consumers see the same call surface.
 */
import { createNewArtifact } from '@/features/artifacts/api/artifacts.api';
import {
  makeUseCreateArtifactVersion,
  makeUseWidgetVersions,
  type UseWidgetVersionsStoreHook,
} from '@/entities/widget';
import { useWidgetStore } from './widget.store';

/**
 * Bind the entity-layer factory to the concrete `useWidgetStore` Zustand
 * hook. The cast narrows `useWidgetStore`'s broad selector type to the
 * factory's structural store-state shape; the runtime call is identical.
 */
export const useWidgetVersions = makeUseWidgetVersions(
  useWidgetStore as unknown as UseWidgetVersionsStoreHook,
);

export const useCreateArtifactVersion = makeUseCreateArtifactVersion(createNewArtifact);
